import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "./store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ───────────────── TYPES ───────────────── */

export type ClassifiedListing = {
  listingId: string;
  brandId: string;
  hostId: string;

  name?: string;
  productId?: number | null;

  stock?: number;
  available?: boolean;
  rate?: number;

  photos?: string[];
  videoUrl?: string;

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  isReviewed: boolean;
  isDraft?: boolean;

  joinDate: string;
  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
};

type ClassifiedState = {
  listings: ClassifiedListing[];
  filteredListings: ClassifiedListing[];
filters: {               
    city: string | null;
    country: string | null;
  };
  currentListing: ClassifiedListing | null;
  unsubscribe?: () => void;
  loading: boolean;
  error: string | null;
    brandListings: ClassifiedListing[];
brandListingsId: string | null;
brandListingsLoading: boolean;
brandUnsubscribe?: () => void;


};

const initialState: ClassifiedState = {
  listings: [],
  currentListing: null,
  unsubscribe: undefined,
  loading: false,
  error: null,
  filteredListings: [],
  filters: {
    city: null,
    country: null
  },
  brandListings: [],
  brandListingsId: null,
  brandListingsLoading: false
};

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseClassified = (
  id: string,
  raw: Record<string, any>
): ClassifiedListing => {
  const { createdAt, updatedAt, ...safeRaw } = raw;

  return {
    listingId: id,
    brandId: raw.brandId ?? "",
    hostId: raw.hostId ?? "",

    name: raw.name ?? "",
    productId: raw.productId ?? null,

    stock: raw.stock ?? 0,
    available: raw.available ?? true,
    rate: raw.rate ?? 0,

    photos: raw.photos ?? [],
    videoUrl: raw.videoUrl ?? "",

    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    isReviewed: raw.isReviewed ?? false,
    isDraft: raw.isDraft ?? true,

    joinDate: raw.joinDate ?? new Date().toISOString(),

    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),

    ...safeRaw,
  };
};

/* ───────────────── UPSERT ───────────────── */

export const upsertClassified = createAsyncThunk<
  ClassifiedListing,
  {
    listingId?: string;
    data: Omit<
      Partial<ClassifiedListing>,
      | "brandId"
      | "hostId"
      | "joinDate"
      | "createdAt"
      | "updatedAt"
      | "photos"
      | "videoUrl"
    >;
  }
>("classifieds/upsert", async ({ listingId, data }, { rejectWithValue }) => {
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated");

    /* ───────── FIND BRAND ───────── */

    const partnerQuery = query(
      collection(db, "commercePartners"),
      where("userId", "==", user.uid)
    );

    const partnerSnap = await getDocs(partnerQuery);
    if (partnerSnap.empty) {
      throw new Error("No classified partner found");
    }

    const brandId = partnerSnap.docs[0].id;

    /* ───────── CLEAN DATA ───────── */

    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    );

    const baseData = {
      ...cleanData,
      brandId,
      hostId: user.uid,
      updatedAt: serverTimestamp(),
    };

    /* ───────── UPDATE ───────── */

    if (listingId) {
      const refDoc = doc(db, "classifieds", listingId);
      await updateDoc(refDoc, baseData);

      const snap = await getDoc(refDoc);
      return parseClassified(snap.id, snap.data() || {});
    }

    /* ───────── CREATE ───────── */

    const payload = {
      ...baseData,
      joinDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      isPublished: cleanData.isPublished ?? false,
      isDraft: cleanData.isDraft ?? !(cleanData.isPublished ?? false),
      isFlagged: false,
      isComplete: false,
      isReviewed: false,
    };

    const colRef = collection(db, "classifieds");
    const newDoc = await addDoc(colRef, payload);
    await updateDoc(newDoc, { listingId: newDoc.id });

    const snap = await getDoc(newDoc);
    return parseClassified(newDoc.id, snap.data() || {});
  } catch (err: any) {
    return rejectWithValue(err.message || "upsert failed");
  }
});

/* ───────────────── LISTENERS ───────────────── */

export const listenToBrandClassified =
  ({ brandId }: { brandId: string }) =>
  (dispatch: AppDispatch) => {
    if (!brandId) return;

    const q = query(
      collection(db, "classifieds"),
      where("brandId", "==", brandId),
      where("isPublished", "==", true)
    );

    return onSnapshot(q, (snap) => {
      dispatch(
        setListings(
          snap.docs.map((d) =>
            parseClassified(d.id, d.data())
          )
        )
      );
    });
  };


  

  export const listenToHostClassified =
  ({ hostId }: { hostId: string }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {

    if (!hostId) return;

    const { unsubscribe } = getState().classifieds;

    // ✅ Always clean previous listener
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, "classifieds"),
      where("hostId", "==", hostId)
    );

    const unsub = onSnapshot(q, (snap) => {
      dispatch(
        setListings(
          snap.docs.map((d) =>
            parseClassified(d.id, d.data())
          )
        )
      );
    });

    dispatch(setUnsubscribe(unsub));
  };



  export const listenToPublishedClassified =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().classifieds;
    if (unsubscribe) return;

    // 1️⃣ Listen to published brands first
    const brandsQuery = query(
      collection(db, "commercePartners"),
      where("isPublished", "==", true)
    );

    const brandsUnsub = onSnapshot(brandsQuery, (brandSnap) => {
      const publishedBrandIds = brandSnap.docs.map((d) => d.id);

      // If no published brands → clear products
      if (!publishedBrandIds.length) {
        dispatch(setListings([]));
        return;
      }

      // 🔥 Firestore "in" limit = 10
      // So we chunk brandIds to multiple queries
      const chunkSize = 10;
      const chunks: string[][] = [];

      for (let i = 0; i < publishedBrandIds.length; i += chunkSize) {
        chunks.push(publishedBrandIds.slice(i, i + chunkSize));
      }

      let allProducts: ClassifiedListing[] = [];
      let listeners: (() => void)[] = [];

      chunks.forEach((chunk) => {
        const productsQuery = query(
          collection(db, "classifieds"),
          where("isPublished", "==", true),
          where("brandId", "in", chunk)
        );

        const unsubProducts = onSnapshot(productsQuery, (snap) => {
          const products = snap.docs.map((d) =>
            parseClassified(d.id, d.data())
          );

          allProducts = [
            ...allProducts.filter(
              (p) => !products.some((np) => np.listingId === p.listingId)
            ),
            ...products,
          ];

          dispatch(setListings(allProducts));
        });

        listeners.push(unsubProducts);
      });

      // save combined unsubscribe
      dispatch(
        setUnsubscribe(() => {
          brandsUnsub();
          listeners.forEach((u) => u());
        })
      );
    });
  };


  

export const listenToClassifiedData =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;

    const { unsubscribe } = getState().classifieds;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const refDoc = doc(db, "classifieds", listingId);

    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) {
        dispatch(setCurrentListing(null));
        return;
      }

      const parsed = parseClassified(snap.id, snap.data());

      dispatch(setCurrentListing(parsed));
    });

    dispatch(setUnsubscribe(unsub));
  };

/* ───────────────── MEDIA ───────────────── */

export const upsertClassifiedMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { listingId?: string; files: File[]; type: "photo" | "video" }
>(
  "classifieds/upsertMedia",
  async ({ listingId, files, type }, { dispatch }) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");

    let id = listingId;
    const uploaded: string[] = [];

    for (const file of files) {
      const folder = type === "photo" ? "photos" : "videos";
      const path = `classifieds/${id || "new"}/${folder}/${file.name}`;

      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      uploaded.push(await getDownloadURL(storageRef));
    }

    if (!id) {
      const created = await dispatch(
        upsertClassified({
          data: {
            photos: type === "photo" ? uploaded : [],
            videoUrl: type === "video" ? uploaded[0] : "",
          },
        })
      ).unwrap();

      id = created.listingId;
    } else {
      await dispatch(
        upsertClassified({
          listingId: id,
          data:
            type === "photo"
              ? { photos: uploaded }
              : { videoUrl: uploaded[0] },
        })
      );
    }

    return { listingId: id!, downloadURLs: uploaded };
  }
);

export const deleteClassifiedPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>(
  "classifieds/deletePhoto",
  async ({ listingId, photoUrl }) => {
    const refDoc = doc(db, "classifieds", listingId);
    const snap = await getDoc(refDoc);

    if (!snap.exists()) throw new Error("Listing not found");

    const photos = snap.data()?.photos || [];

    await updateDoc(refDoc, {
      photos: photos.filter((p: string) => p !== photoUrl),
    });

    if (photoUrl.includes("/o/")) {
      const encoded = photoUrl.split("/o/")[1].split("?")[0];
      await deleteObject(ref(storage, decodeURIComponent(encoded)));
    }

    return photoUrl;
  }
);

/* ───────────────── DELETE ───────────────── */

export const deleteClassified = createAsyncThunk<
  { listingId: string },
  string
>("classifieds/delete", async (listingId) => {
  await deleteDoc(doc(db, "classifieds", listingId));
  return { listingId };
});

export const listenToProductsByCategory =
  (categoryId: number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryId) return;

    const { unsubscribe } = getState().classifieds;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, "classifieds"),
      where("isPublished", "==", true),
      where("categoryId", "==", categoryId)
    );

  const unsub = onSnapshot(
  q,
  (snap) => {
    const products = snap.docs.map((d) =>
      parseClassified(d.id, d.data())
    );

    if (products.length === 0) {
      // 🔥 AUTO FALLBACK
      dispatch(listenToPublishedClassified());
      return;
    }

    dispatch(setListings(products));
  },
  (error) => {
    dispatch(setError(error.message));
  }
);


    dispatch(setUnsubscribe(unsub));
  };

export const listenToCategoryGroup =
  (categoryIds: number[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {

    if (!categoryIds.length) return;

    const { unsubscribe } = getState().classifieds;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, "classifieds"),
      where("isPublished", "==", true),
      where("categoryId", "in", categoryIds)
    );

    const unsub = onSnapshot(q, (snap) => {
      dispatch(
        setListings(
          snap.docs.map((d) =>
            parseClassified(d.id, d.data())
          )
        )
      );
    });

    dispatch(setUnsubscribe(unsub));
  };


  export const filterProductsByBrandLocation =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { listings, filters } = getState().classifieds;
    const { partners } = getState().commercePartners;

    if (!filters.city && !filters.country) {
      dispatch(setFilteredListings(listings));
      return;
    }

    const filtered = listings.filter((product) => {
      const brand = partners.find(
        (p) => p.brandId === product.brandId
      );

      if (!brand) return false;

      const cityMatch = filters.city
        ? brand.address?.city
            ?.toLowerCase()
            .includes(filters.city.toLowerCase())
        : true;

      const countryMatch = filters.country
        ? brand.address?.country
            ?.toLowerCase()
            .includes(filters.country.toLowerCase())
        : true;

      return cityMatch && countryMatch;
    });

    dispatch(setFilteredListings(filtered));
  };


export const filterLocalProductsByCountry =
  (country: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {

    if (!country) {
      dispatch(setFilteredListings(getState().classifieds.listings));
      return;
    }

    try {
      // 🔥 1️⃣ Fetch brands in that country directly from Firestore
      const partnersQuery = query(
        collection(db, "commercePartners"),
        where("address.country", "==", country)
      );

      const partnersSnap = await getDocs(partnersQuery);

      const localBrandIds = partnersSnap.docs.map(doc => doc.id);

      if (!localBrandIds.length) {
        dispatch(setFilteredListings([]));
        return;
      }

      // 🔥 2️⃣ Filter products by matching brandId
      const { listings } = getState().classifieds;

      const filtered = listings.filter(product =>
        localBrandIds.includes(product.brandId)
      );

      dispatch(setFilteredListings(filtered));

    } catch (error: any) {
      dispatch(setError(error.message));
    }
  };


/* ─────────────────────────────────────────────
   listenToBrandProducts
   Scoped to one brandId → writes to brandListings only.
   Never touches global listings / filteredListings.
─────────────────────────────────────────────── */
export const listenToBrandProducts =
  (brandId: string) =>
  (dispatch: AppDispatch, getState: () => RootState): (() => void) => {
    if (!brandId) return () => {};

    dispatch(setBrandListingsLoading(true));
    dispatch(setBrandListingsId(brandId));

const q = query(
  collection(db, "classifieds"),
  where("brandId", "==", brandId),
  where("isPublished", "==", true)
);

const unsub = onSnapshot(
  q,
  (snap) => {
    const products: ClassifiedListing[] = snap.docs.map((d) =>
      parseClassified(d.id, d.data())
    );

    dispatch(setBrandListings(products));
    dispatch(setBrandListingsLoading(false));
  },
  (err) => {
    dispatch(setError(err.message));
    dispatch(setBrandListingsLoading(false));
  }
);
    

    dispatch(setBrandUnsubscribe(unsub));
    return unsub;
  };

export const stopBrandProductsListener =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { brandUnsubscribe } = getState().classifieds;
    if (brandUnsubscribe) {
      brandUnsubscribe();
      dispatch(setBrandUnsubscribe(undefined));
      dispatch(setBrandListingsId(null));
      dispatch(clearBrandListings());
    }
  };


/* ───────────────── SLICE ───────────────── */

const classifiedsSlice = createSlice({
  name: "classifieds",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<ClassifiedListing[]>) => {
      state.listings = action.payload;
    },
    setCurrentListing: (
      state,
      action: PayloadAction<ClassifiedListing | null>
    ) => {
      state.currentListing = action.payload;
    },

    setFilteredListings: (
  state,
  action: PayloadAction<ClassifiedListing[]>
) => {
  state.filteredListings = action.payload;
},

    setUnsubscribe: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

  setFilters: (
  state,
  action: PayloadAction<{ city?: string | null; country?: string | null }>
) => {
  state.filters = {
    ...state.filters,
    ...action.payload,
  };
},
resetClassifiedFilters: (state) => {
  state.filters = {
    city: null,
    country: null,
  };
  state.filteredListings = state.listings;
},

    setBrandListings: (state, a: PayloadAction<ClassifiedListing[]>) => {
  state.brandListings = a.payload;
},
clearBrandListings: (state) => {
  state.brandListings = [];
},
setBrandListingsId: (state, a: PayloadAction<string | null>) => {
  state.brandListingsId = a.payload;
},
setBrandListingsLoading: (state, a: PayloadAction<boolean>) => {
  state.brandListingsLoading = a.payload;
},
setBrandUnsubscribe: (state, a: PayloadAction<(() => void) | undefined>) => {
  state.brandUnsubscribe = a.payload;
},


  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertClassified.fulfilled, (state, action) => {
        const idx = state.listings.findIndex(
          (l) => l.listingId === action.payload.listingId
        );
        if (idx !== -1) state.listings[idx] = action.payload;
        else state.listings.push(action.payload);
      })
      .addCase(deleteClassified.fulfilled, (state, action) => {
        state.listings = state.listings.filter(
          (l) => l.listingId !== action.payload.listingId
        );
      });
  },
});


export const {
  setListings,
  setCurrentListing,
  setUnsubscribe,
  setError,
  setFilters,
  setFilteredListings,
      setBrandListings,
  clearBrandListings,
  setBrandListingsId,
  setBrandListingsLoading,
  setBrandUnsubscribe
} = classifiedsSlice.actions;


export default classifiedsSlice.reducer;
function firestore() {
  throw new Error("Function not implemented.");
}

